export const name = 'rnLocalStorage';
/**
 * @ngdoc service
 * @name rhino.components.local-storage.service:rnLocalStorage
 * @description
 * Provides a simple facade over localStorage, allowing it to be easily
 * injected and testable.  Also simplifies storing and retrieving objects
 * instead of just strings.
 *
 * @example
 * ```js
 * rnLocalStorage.getString('marco'); // returns null
 * rnLocalStorage.setString('marco', 'polo');
 * rnLocalStorage.getString('marco'); // returns 'polo'
 *
 * rnLocalStorage.clear();
 * rnLocalStorage.getString('marco'); // returns null again
 *
 * rnLocalStorage.getObject('preferences'); // returns null
 * rnLocalStorage.setObject('preferences', {lang: 'en'});
 * rnLocalStorage.getObject('preferences'); // returns {lang: 'en'}
 *
 * rnLocalStorage.setString('notAnObject', 'notAnObject');
 * rnLocalStorage.getObject('notAnObject'); // throws JSON parse error
 * ```
 */
export default function localStorageService($window) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let localStorage = $window.localStorage;

  let service = {
    getString: getString,
    setString: setString,
    getObject: getObject,
    setObject: setObject,
    removeItem: removeItem,
    clear: clear,
  };
  return service;

  ////////////////

  /**
   * @ngdoc function
   * @name getString
   * @methodOf rhino.components.local-storage.service:rnLocalStorage
   * @param {string} key to get
   * @returns {string} value `null` if not found
   */
  function getString(key) {
    return localStorage.getItem(key);
  }

  /**
   * @ngdoc function
   * @name setString
   * @methodOf rhino.components.local-storage.service:rnLocalStorage
   * @param {string} key to set
   * @param {string} str value to set
   */
  function setString(key, str) {
    localStorage.setItem(key, str);
  }

  /**
   * @ngdoc function
   * @name getObject
   * @methodOf rhino.components.local-storage.service:rnLocalStorage
   * @param {string} key to get
   * @returns {object} value `null` if not found
   */
  function getObject(key) {
    let item = getString(key);
    if(item === null) {
      return null;
    }
    return JSON.parse(item);
  }

  /**
   * @ngdoc function
   * @name setObject
   * @methodOf rhino.components.local-storage.service:rnLocalStorage
   * @param {string} key to set
   * @param {object} value to set
   */
  function setObject(key, obj) {
    setString(key, JSON.stringify(obj));
  }

  /**
   * @ngdoc function
   * @name removeItem
   * @methodOf rhino.components.local-storage.service:rnLocalStorage
   */
  function removeItem(key) {
    localStorage.removeItem(key);
  }

  /**
   * @ngdoc function
   * @name clear
   * @methodOf rhino.components.local-storage.service:rnLocalStorage
   */
  function clear() {
    localStorage.clear();
  }
}
